import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, Link2, Copy, Check, AlertTriangle, CalendarCheck2, Power } from 'lucide-react';
import api from '../../../utils/api';
import { Button, Modal, errMsg } from '../kit';

// The Email calendar's "In your other calendars": two-way sync with an Outlook calendar (a
// Microsoft mailbox signed in with calendar permission), and a private link that Google Calendar,
// Apple Calendar or Outlook can subscribe to.
const ago = (iso) => {
  if (!iso) return 'not yet';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

export function useCalendarSync() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get('/calendar/sync').then((r) => setData(r.data)).catch(() => setData({ feed: { on: false }, mailboxes: [] })), []);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

export default function OtherCalendars({ onSynced }) {
  const [data, load] = useCalendarSync();
  const [busy, setBusy] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const ms = ((data && data.mailboxes) || []).filter((m) => m.provider === 'microsoft');
  const connect = async (visibility) => {
    setBusy('connect');
    try {
      const r = await api.post('/mail/accounts/oauth/microsoft/start', { calendar: true, ...(visibility ? { visibility } : {}) });
      window.location.assign(r.data.url);
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  const now = async (m) => {
    setBusy(m.id);
    try { const r = await api.post(`/calendar/sync/${m.id}/now`); toast.success(`In step with Outlook${r.data.moved ? `: ${r.data.moved} changed there came back` : ''}`); await load(); onSynced && onSynced(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const off = async (m) => {
    if (!window.confirm(`Stop keeping ${m.email}'s Outlook calendar in step? Rostery's appointments are taken out of that Outlook calendar (they stay in Rostery).`)) return;
    setBusy(m.id);
    try { await api.post(`/calendar/sync/${m.id}/off`); toast.success('Outlook calendar sync is off'); await load(); onSynced && onSynced(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  if (!data) return null;
  return (
    <section aria-labelledby="cal-other">
      <h3 id="cal-other" className="text-[12.5px] font-semibold text-slate-800 mb-1.5">In your other calendars</h3>
      <ul className="space-y-2">
        {ms.map((m) => (
          <li key={m.id} className="rounded-xl ring-1 ring-slate-200/80 p-2.5">
            <p className="text-[12.5px] font-semibold text-slate-800 truncate" title={m.email}>Outlook · {m.email}</p>
            {m.calendar_sync ? (
              <>
                <p className="text-[11.5px] text-slate-500 mt-0.5 flex items-center gap-1">
                  {m.calendar_error ? <><AlertTriangle size={12} className="text-amber-600" aria-hidden /><span className="text-amber-800">{m.calendar_error}</span></> : <><CalendarCheck2 size={12} className="text-emerald-600" aria-hidden />Two-way, checked {ago(m.calendar_synced_at)}</>}
                </p>
                <div className="flex gap-1.5 mt-2">
                  <Button size="sm" variant="secondary" icon={RefreshCw} loading={busy === m.id} onClick={() => now(m)}>Sync now</Button>
                  <Button size="sm" variant="ghost" icon={Power} onClick={() => off(m)}>Turn off</Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-[11.5px] text-slate-500 mt-0.5">Not in step. Sign in once more to let Rostery use this Outlook calendar.</p>
                <Button size="sm" className="mt-2" loading={busy === 'connect'} onClick={() => connect()}>Keep Outlook in step</Button>
              </>
            )}
          </li>
        ))}
        {!ms.length && (
          <li className="rounded-xl ring-1 ring-slate-200/80 p-2.5">
            <p className="text-[12.5px] font-semibold text-slate-800">Outlook (Microsoft 365)</p>
            <p className="text-[11.5px] text-slate-500 mt-0.5">Your Rostery appointments in Outlook, and changes made there come back.</p>
            <Button size="sm" className="mt-2" loading={busy === 'connect'} onClick={() => connect('owner')}>Connect Outlook calendar</Button>
          </li>
        )}
        <li className="rounded-xl ring-1 ring-slate-200/80 p-2.5">
          <p className="text-[12.5px] font-semibold text-slate-800">Google, Apple and others</p>
          <p className="text-[11.5px] text-slate-500 mt-0.5">{data.feed && data.feed.on ? `Your calendar link is on${data.feed.last_used_at ? `, last read ${ago(data.feed.last_used_at)}` : ''}.` : 'A private link to add your appointments to Google Calendar or any calendar app.'}</p>
          <Button size="sm" variant="secondary" icon={Link2} className="mt-2" onClick={() => setLinkOpen(true)}>{data.feed && data.feed.on ? 'Your calendar link' : 'Get my calendar link'}</Button>
        </li>
      </ul>
      <FeedDialog open={linkOpen} onClose={() => setLinkOpen(false)} feed={data.feed} onChanged={load} />
    </section>
  );
}

function FeedDialog({ open, onClose, feed, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const set = async (body, msg) => {
    setBusy(true);
    try { await api.post('/calendar/feed', body); await onChanged(); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(feed.url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { toast.error('Select the link and copy it.'); }
  };
  const on = feed && feed.on;
  return (
    <Modal open={open} onClose={onClose} title="Your calendar in Google, Apple or Outlook" width="lg"
      sub="A private link to your Rostery appointments (the ones on your calendar, and meetings you are invited to), with their reminders."
      footer={on ? <><Button variant="ghost" className="mr-auto text-rose-600" disabled={busy} onClick={() => set({ on: false }, 'Calendar link turned off')}>Turn the link off</Button><Button variant="secondary" disabled={busy} onClick={() => set({ rotate: true }, 'New link made. The old one no longer works.')}>Make a new link</Button><Button onClick={onClose}>Done</Button></> : <><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => set({ on: true })}>Make my link</Button></>}>
      {on ? (
        <div className="space-y-4">
          <div className="flex gap-2">
            <input readOnly value={feed.url} onFocus={(e) => e.target.select()} aria-label="Your calendar link" className="flex-1 min-w-0 h-10 rounded-lg ring-1 ring-inset ring-slate-300 bg-slate-50 px-3 text-[12.5px] font-mono text-slate-700" />
            <Button icon={copied ? Check : Copy} onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 text-[12.5px] text-slate-700">
            <div className="rounded-xl bg-slate-50 p-3"><p className="font-semibold text-slate-900 mb-1">Google Calendar</p>On a computer: Other calendars, <b>+</b>, <b>From URL</b>, paste the link, <b>Add calendar</b>.</div>
            <div className="rounded-xl bg-slate-50 p-3"><p className="font-semibold text-slate-900 mb-1">Outlook</p><b>Add calendar</b>, <b>Subscribe from web</b>, paste the link, <b>Import</b>. (With Microsoft 365, "Keep Outlook in step" is better: it works both ways.)</div>
            <div className="rounded-xl bg-slate-50 p-3"><p className="font-semibold text-slate-900 mb-1">iPhone and Mac</p>Settings › Calendar › Accounts › Add account › Other › <b>Add subscribed calendar</b>, paste the link.</div>
          </div>
          <p className="text-[12px] text-slate-600 bg-amber-50 ring-1 ring-amber-200 rounded-lg px-3 py-2">
            Treat this link like a password: anyone with it can see your appointment titles and times. It never shows places, notes or participants' details. Google updates subscribed calendars every few hours; changes are made in Rostery.
          </p>
        </div>
      ) : (
        <p className="text-[13.5px] text-slate-700">Rostery makes a private link for you. Add it once to Google Calendar, Apple Calendar or Outlook and your appointments appear there, with Rostery's reminders, and stay up to date.</p>
      )}
    </Modal>
  );
}
